import { useEffect, useState } from 'react';
import { listEvents, type Event } from '../../services/events';
import { AttackCategoryBadge } from '../../components/AttackCategoryBadge';
import { humanizarEvento, corDoTom, rotuloDoTom } from '../../lib/eventHumanizer';

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-red-900 text-red-300 border-red-700',
  high: 'bg-orange-900 text-orange-300 border-orange-700',
  suspect: 'bg-amber-900 text-amber-300 border-amber-700',
  info: 'bg-blue-900 text-blue-300 border-blue-700',
};

const CATEGORIES = [
  'PACKET_SNIFFER', 'MEMORY_HACK', 'DEBUGGER', 'INJECTION',
  'GAME_TAMPERING', 'KERNEL_TOOLING', 'RECON', 'OVERLAY',
  'TIMING', 'INTEGRITY', 'UNKNOWN',
];

export function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [severity, setSeverity] = useState<'all' | 'high' | 'critical'>('all');
  // Sessão 1 Frente A — filters novos
  const [category, setCategory] = useState<string>('');
  const [sourceProgramName, setSourceProgramName] = useState<string>('');
  const [aggregatedOnly, setAggregatedOnly] = useState<boolean>(false);
  const [minOccurrenceCount, setMinOccurrenceCount] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listEvents({
      limit: 100,
      severity: severity === 'all' ? undefined : severity,
      category: category || undefined,
      source_program_name: sourceProgramName || undefined,
      aggregated_only: aggregatedOnly || undefined,
      min_occurrence_count: minOccurrenceCount ? Number(minOccurrenceCount) : undefined,
    })
      .then((r) => {
        if (!cancelled) setEvents(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'erro ao carregar eventos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [severity, category, sourceProgramName, aggregatedOnly, minOccurrenceCount]);

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-bold text-gray-200 mb-3">Eventos de detecção</h2>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Severity</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as 'all' | 'high' | 'critical')}
              className="bg-[#161a1f] border border-[#2a2f36] rounded px-3 py-1 text-sm text-gray-300"
            >
              <option value="all">Todas as severidades</option>
              <option value="high">Apenas high+</option>
              <option value="critical">Apenas critical</option>
            </select>
          </div>

          {/* Sessão 1 Frente A — Category filter (§6.6) */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Attack category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-[#161a1f] border border-[#2a2f36] rounded px-3 py-1 text-sm text-gray-300"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>

          {/* Source program name search */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Source program name</label>
            <input
              type="text"
              value={sourceProgramName}
              onChange={(e) => setSourceProgramName(e.target.value)}
              placeholder="wpepro.exe"
              className="bg-[#161a1f] border border-[#2a2f36] rounded px-3 py-1 text-sm text-gray-300 w-48"
            />
          </div>

          {/* Min occurrence count */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Min occurrence count</label>
            <input
              type="number"
              min={1}
              value={minOccurrenceCount}
              onChange={(e) => setMinOccurrenceCount(e.target.value)}
              placeholder="1"
              className="bg-[#161a1f] border border-[#2a2f36] rounded px-3 py-1 text-sm text-gray-300 w-24"
            />
          </div>

          {/* Aggregated only checkbox */}
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={aggregatedOnly}
              onChange={(e) => setAggregatedOnly(e.target.checked)}
              className="accent-blue-500"
            />
            Aggregated only (occurrence_count &gt; 1)
          </label>
        </div>
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : events.length === 0 ? (
        <p className="text-gray-500">Nenhum evento.</p>
      ) : (
        <div className="space-y-2">
          {events.map((e) => (
            <div
              key={e.id}
              className="bg-[#161a1f] border border-[#2a2f36] rounded p-3 hover:border-[#3a3f46] transition"
            >
              {/* Frase humana em primeiro plano: o operador bate o olho e
                  entende O QUE ACONTECEU. O código técnico continua logo
                  abaixo, para quem precisa dele. */}
              {(() => {
                const h = humanizarEvento(e.event_type, e.payload);
                return (
                  <div className="mb-2">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <p className="text-sm text-gray-100 font-medium leading-snug">
                        {h.titulo}
                      </p>
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded border shrink-0 ${corDoTom(h.tom)}`}
                        title="Peso do sinal — 'não é sobre o jogador' marca evento operacional do sistema"
                      >
                        {rotuloDoTom(h.tom)}
                      </span>
                    </div>
                    {h.detalhe && (
                      <p className="text-xs text-gray-400 mt-0.5">{h.detalhe}</p>
                    )}
                    {h.significado && (
                      <p className="text-xs text-gray-500 mt-0.5 italic">{h.significado}</p>
                    )}
                  </div>
                );
              })()}
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-xs px-2 py-0.5 rounded border ${
                      SEVERITY_COLOR[e.severity_label] || 'bg-gray-800 text-gray-400 border-gray-700'
                    }`}
                  >
                    {e.severity_label}
                  </span>
                  {/* Sessão 1 Frente A — attack_category badge */}
                  {e.attack_category && <AttackCategoryBadge category={e.attack_category} />}
                  <code className="text-xs text-gray-500">{e.event_type}</code>
                  {e.payload?.method !== undefined && (
                    <span className="text-xs text-gray-600">
                      method=<code>{String(e.payload.method)}</code>
                    </span>
                  )}
                  {/* Sessão 1 Frente A — occurrence_count badge se > 1 */}
                  {e.occurrence_count && e.occurrence_count > 1 && (
                    <span className="text-xs px-2 py-0.5 rounded bg-purple-900 text-purple-200" title="Aggregated occurrences in window">
                      ×{e.occurrence_count}
                    </span>
                  )}
                  {/* Sessão 1 Frente A — source_program */}
                  {e.source_program?.name && (
                    <span className="text-xs px-2 py-0.5 rounded bg-rose-900 text-rose-200" title={e.source_program.signer_subject || ''}>
                      🎯 {e.source_program.name}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(e.last_seen_at || e.created_at).toLocaleString()}
                </span>
              </div>
              {Object.keys(e.payload).length > 0 && (
                <details className="mt-1">
                  <summary className="text-xs text-gray-500 cursor-pointer select-none hover:text-gray-300">
                    dados técnicos
                  </summary>
                  <pre className="text-xs text-gray-400 bg-[#0d0f12] rounded p-2 mt-1 overflow-x-auto">
                    {JSON.stringify(e.payload, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
